import * as Yup from 'yup';
import dayjs from 'dayjs';
import { CustomerValidation, DraftCustomerValidation } from './CustomerValidation';
import { getCurrentDateInUtc, getCurrentTimeInMysTzFormatted, getDateInUtc } from '../utils/formatDate';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
export const RefundsValidation = Yup.object().shape({
  document_number: Yup.string().when('$auto_document_number_generation', {
    is: false,
    then: (schema) =>
      schema
        .required('Document number is required')
        .test('log-debug', 'DEBUG', function (value) {
          console.log('auto_document_number_generation context:', this.options.context.auto_document_number_generation);
          return true;
        })
        .max(50, 'Document number cannot be more than 50 characters'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  document_date: Yup.date()
    .required('Document date is required')
    .test('is-within-past-72-hours', 'Document date must be within 72 hours from now', function (value) {
      if (!value) return false;
      const now = getCurrentDateInUtc();
      const documentDate = getDateInUtc(
        dayjs.tz(value, 'Asia/Kuala_Lumpur').format('YYYY-MM-DD'),
        getCurrentTimeInMysTzFormatted(),
      );
      if (documentDate.isAfter(now)) {
        return false;
      }
      const diff = now.diff(documentDate, 'hour');
      return diff <= 72;
    }),
  invoice_number: Yup.string()
    .required('Invoice number is required')
    .max(50, 'Invoice number cannot be more than 50 characters'),
  billed_by: Yup.object().required('Billed by is required'),
  billed_to: Yup.object()
    .nullable()
    .when('$isDraft', {
      is: false,
      then: () =>
        Yup.mixed()
          .required('billed_to is required')
          .test('is-valid-customer', 'Invalid customer details', async function (value) {
            const { item_details } = this.options.parent || {};
            const hasConsolidateItem = item_details?.some((item) => item.item_code === '004');
            const phoneSchema = hasConsolidateItem
              ? Yup.string()
                  .required('Phone number is required')
                  .test(
                    'is-phone-or-na',
                    'Phone number must be at least 8 characters or "NA"',
                    (val) => val === 'NA' || val?.length >= 8,
                  )
              : Yup.string().required('Phone number is required').min(8, 'Phone number must be at least 8 characters');

            const registrationSchema = hasConsolidateItem
              ? Yup.string().oneOf(
                  ['NA'],
                  `Only 'NA' is allowed for Registration number when the Item Classification Code is Consolidated e-Invoice.`,
                )
              : Yup.string()
                  .required('Registration number is required')
                  .max(20, 'Registration number must be at most 20 characters')
                  .notOneOf(['NA'], 'NA is not a valid registration number for e-invoice');

            const tinSchema = hasConsolidateItem
              ? Yup.string()
                  .required('TIN is required')
                  .max(14, 'TIN must be at most 14 characters')
                  .matches(/^[A-Za-z]{1,2}\d{1,12}$/, 'TIN must start with 1–2 letters followed by up to 12 digits')
                  .oneOf(
                    ['EI00000000010', 'EI00000000020', 'EI00000000030', 'EI00000000040'],
                    'Only General Tins are allowed when the Item Classification Code is Consolidated e-Invoice.',
                  )
              : Yup.string()
                  .required('TIN is required')
                  .max(14, 'TIN must be at most 14 characters')
                  .matches(/^[A-Za-z]{1,2}\d{1,12}$/, 'TIN must start with 1–2 letters followed by up to 12 digits');
            try {
              const patchedValue = {
                ...value,
                country_code:
                  typeof value?.country_code === 'string' ? { code: value.country_code } : value.country_code,
                state: typeof value?.state === 'string' ? value.state : { code: value.state },
                registration_type:
                  typeof value?.registration_type === 'string'
                    ? { code: value.registration_type }
                    : value.registration_type,
              };

              const dynamicCustomerValidation = CustomerValidation.shape({
                phone: phoneSchema,
                registration_number: registrationSchema,
                tin: tinSchema,
              });
              await dynamicCustomerValidation.validate(patchedValue, { abortEarly: false });
              return true;
            } catch (err) {
              const errorMessages = err.inner?.map((e) => e.message).join('\n');
              return this.createError({ message: errorMessages || 'Invalid billed to details' });
            }
          }),
      otherwise: () =>
        Yup.mixed()
          .nullable()
          .test('is-valid-customer-if-present', 'Invalid customer details', async function (value) {
            if (!value) return true;
            try {
              const patchedValue = {
                ...value,
                country_code:
                  typeof value?.country_code === 'string' ? { code: value.country_code } : value.country_code,
                state: typeof value?.state === 'string' ? value.state : { code: value.state },
                registration_type:
                  typeof value?.registration_type === 'string'
                    ? { code: value.registration_type }
                    : value.registration_type,
              };
              await DraftCustomerValidation.validate(patchedValue, { abortEarly: false });
              return true;
            } catch (err) {
              const errorMessages = err.inner?.map((e) => e.message).join('\n');
              return this.createError({ message: errorMessages || 'Invalid billed to details' });
            }
          }),
    }),
  document_currency_code: Yup.string().required('Currency is required'),
  conversion_rate_per_myr: Yup.mixed().when('document_currency_code', {
    is: (val) => val !== 'MYR',
    then: () =>
      Yup.number()
        .typeError('Must be a valid number')
        .required('Conversion rate for Non-MYR currency is required')
        .min(0.00000001, 'Must be greater than 0.00000001'),
    otherwise: () => Yup.number().nullable(),
  }),
  item_details: Yup.array().required('Item details is required').min(1, 'Item details is required'),
  payment_terms: Yup.string().max(300, 'Payment terms must not more than 300 characters'),
});

export default RefundsValidation;
